/**
 * Toasts — transient event notices over the map.
 *
 * The naive version showed an event only while the playhead sat inside its
 * span. At 8x a warning covering forty rows flashes past in half a second,
 * which is not long enough to read, and a warning covering one row is
 * essentially invisible.
 *
 * So a toast dwells for a fixed wall-clock time regardless of playback speed,
 * and several stack. Reading time is a property of the reader, not of the
 * flight.
 */

const DWELL_MS = 4500;
const MAX_VISIBLE = 5;

export class Toasts {
  constructor(container, formatTime) {
    this.container = container;
    this.formatTime = formatTime;
    this.active = new Map();
    this.nodes = new Map();
    this._timer = null;
  }

  /**
   * Show an event, or refresh it if it is already on screen.
   *
   * Keyed on the event's own row index so that scrubbing back and forth over
   * the same warning does not stack forty copies of it.
   */
  push(event) {
    const key = `${event.index}:${event.severity}`;
    const existing = this.active.get(key);

    if (existing) {
      existing.expires = Date.now() + DWELL_MS;
      return;
    }

    this.active.set(key, { event, expires: Date.now() + DWELL_MS });
    this.render();
    this.ensureTimer();
  }

  /** Drop anything past its dwell time. */
  sweep() {
    const now = Date.now();
    let changed = false;

    for (const [key, entry] of this.active) {
      if (entry.expires <= now) {
        this.active.delete(key);
        changed = true;
      }
    }

    if (changed) this.render();
    if (this.active.size === 0) this.stopTimer();
  }

  ensureTimer() {
    if (this._timer) return;
    this._timer = setInterval(() => this.sweep(), 250);
  }

  stopTimer() {
    if (!this._timer) return;
    clearInterval(this._timer);
    this._timer = null;
  }

  /**
   * Reconcile the DOM against the active set.
   *
   * Nodes are added and removed individually rather than rewriting innerHTML.
   * A wholesale rebuild cannot animate: every surviving toast is destroyed and
   * recreated, which restarts its entrance and makes the stack flicker, and
   * departing toasts vanish with no chance to fade.
   *
   * Ordering is done with the CSS `order` property so a new critical notice
   * does not physically move its neighbours in the DOM, which would interrupt
   * their transitions.
   */
  render() {
    const weight = { critical: 0, warning: 1, info: 2 };
    const wanted = [...this.active.entries()]
      .sort(
        ([, a], [, b]) =>
          weight[a.event.severity] - weight[b.event.severity] || b.expires - a.expires
      )
      .slice(0, MAX_VISIBLE);

    const keep = new Set(wanted.map(([key]) => key));

    for (const [key, node] of this.nodes) {
      if (!keep.has(key)) this.retire(key, node);
    }

    wanted.forEach(([key, entry], position) => {
      let node = this.nodes.get(key);

      if (!node) {
        node = this.build(entry.event);
        this.nodes.set(key, node);
        this.container.appendChild(node);

        // Force a reflow so the browser registers the starting state before
        // the class flips, otherwise it coalesces both and nothing animates.
        void node.offsetHeight;
        node.classList.add('shown');
      }

      node.style.order = String(position);
    });
  }

  build(event) {
    const node = document.createElement('div');
    node.className = `toast ${event.severity}`;
    node.innerHTML = `
      <span class="toast-icon" aria-hidden="true">${ICONS[event.severity] || ICONS.info}</span>
      <span class="toast-body">
        <span class="text">${escapeHtml(event.text)}</span>
        <span class="when">${this.formatTime(event.timeS)}${
          event.repeatCount > 1 ? ` · ${event.repeatCount} rows` : ''
        }</span>
      </span>`;
    return node;
  }

  /** Fade a toast out, then remove it once the transition has finished. */
  retire(key, node) {
    this.nodes.delete(key);
    node.classList.remove('shown');
    node.classList.add('leaving');

    const remove = () => node.remove();
    node.addEventListener('transitionend', remove, { once: true });

    // Belt and braces: transitionend does not fire if the element is hidden or
    // motion is reduced, which would otherwise leak nodes into the container.
    setTimeout(remove, 400);
  }

  clear() {
    this.active.clear();
    this.nodes.clear();
    this.container.innerHTML = '';
    this.stopTimer();
  }

  destroy() {
    this.clear();
  }
}

const ICONS = {
  critical:
    '<svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1.5 15 14H1L8 1.5Zm0 4.2a.8.8 0 0 0-.8.85l.2 3.1a.6.6 0 0 0 1.2 0l.2-3.1A.8.8 0 0 0 8 5.7Zm0 5.3a.85.85 0 1 0 0 1.7.85.85 0 0 0 0-1.7Z"/></svg>',
  warning:
    '<svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1.5 15 14H1L8 1.5Zm0 4.2a.8.8 0 0 0-.8.85l.2 3.1a.6.6 0 0 0 1.2 0l.2-3.1A.8.8 0 0 0 8 5.7Zm0 5.3a.85.85 0 1 0 0 1.7.85.85 0 0 0 0-1.7Z"/></svg>',
  info: '<svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="8" r="6.8"/><rect x="7.2" y="6.6" width="1.6" height="5" rx="0.8" fill="#0a0d10"/><circle cx="8" cy="4.7" r="0.95" fill="#0a0d10"/></svg>',
};

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
